import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { formatApiErrorDetail } from '../utils/api';
import { CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

export const MagicLinkVerifyPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const { t } = useI18n();
  const [status, setStatus] = useState('verifying'); // verifying | ok | error
  const [error, setError] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) { setStatus('error'); setError('Missing sign-in token.'); return; }
    verifyMagicLink(token)
      .then((user) => {
        setStatus('ok');
        // Auto-redirect after a brief celebration moment
        setTimeout(() => {
          navigate(user.role === 'admin' ? '/admin/dashboard' : '/account', { replace: true });
        }, 1200);
      })
      .catch((err) => {
        setStatus('error');
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="magic-verify-page" className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-10 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{t('magic.verifying')}</h1>
          </>
        )}
        {status === 'ok' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">{t('magic.welcome')}</h1>
            <p className="text-slate-600 mb-6">Redirecting you…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">Sign-in link not valid</h1>
            <p data-testid="magic-error" className="text-slate-600 mb-6">{error || t('magic.error')}</p>
            <Link
              to="/login"
              data-testid="magic-request-new-link"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-full font-semibold hover:bg-amber-600 shadow-sm"
            >
              {t('magic.request_new')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
